import { useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";
import LeadAvatar from "@/components/lead-avatar/lead-avatar";
import { cn } from "@/lib/utils";
import type { SmsMessage } from "./use-sms-thread";

type Person = { name: string; color: string; avatar?: string | null };

type Props = {
  /** Oldest first. */
  messages: SmsMessage[];
  lead: Person;
  me: Person;
  total: number;
  loadingMore: boolean;
  onLoadMore: () => void;
  /** Shown when there are no texts yet. */
  emptyText: string;
};

const CompactRow = ({ message, person }: { message: SmsMessage; person: Person }) => {
  const out = message.direction === "outbound";
  return (
    <div className={cn("flex items-end gap-1.5", out && "flex-row-reverse")}>
      <LeadAvatar name={person.name} avatarUrl={person.avatar} color={person.color} gradient className="h-6 w-6 rounded-lg text-[9px] ring-1 ring-white/10" />
      <div
        className={cn(
          "max-w-[80%] whitespace-pre-wrap break-words rounded-xl px-2.5 py-1.5 text-[11.5px] leading-snug",
          out
            ? "bg-brand-500/20 text-white ring-1 ring-brand-500/25"
            : "bg-white/[0.04] text-slate-200 ring-1 ring-white/[0.06]"
        )}
      >
        {message.body}
      </div>
    </div>
  );
};

/**
 * A lead's texts as small avatar + bubble rows for narrow places such as the call popup, oldest
 * first and kept scrolled to the newest. Presentational: feed it from `useSmsConversation`.
 */
const SmsCompactConversation = ({ messages, lead, me, total, loadingMore, onLoadMore, emptyText }: Props) => {
  const listRef = useRef<HTMLDivElement>(null);
  const newestId = messages[messages.length - 1]?.id;

  // Follow a new text, not older ones loaded above the list.
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [newestId]);

  if (messages.length === 0) {
    return <div className="py-4 text-center text-[11.5px] italic text-slate-500">{emptyText}</div>;
  }

  return (
    <div ref={listRef} className="soft-scrollbar max-h-[150px] space-y-2 overflow-y-auto pr-1 scroll-smooth">
      {messages.length < total && (
        <button
          type="button"
          onClick={onLoadMore}
          disabled={loadingMore}
          className="inline-flex h-7 w-full items-center justify-center gap-1.5 rounded-lg border border-white/[0.06] bg-white/[0.02] text-[11px] font-medium text-slate-300 transition hover:border-white/[0.14] hover:bg-white/[0.05] hover:text-white disabled:opacity-50"
        >
          {loadingMore && <Loader2 className="h-3 w-3 animate-spin" />}
          Load more ({messages.length} of {total})
        </button>
      )}
      {messages.map((m) => (
        <CompactRow key={m.id} message={m} person={m.direction === "outbound" ? me : lead} />
      ))}
    </div>
  );
};

export default SmsCompactConversation;
